"use client";
import React, { createContext, useContext, useDebugValue, useState, useCallback, useMemo } from 'react';
import NextScript from 'next/script.js';

const HUBSPOT_LOADED_EVENT = "hubspot_loaded";
// https://github.com/vercel/next.js/issues/46078
const Script = NextScript;
const HubspotContext = createContext({
    isScriptLoaded: false,
    isScriptError: false,
    scriptError: null,
});
const useHubspotContext = () => {
    const values = useContext(HubspotContext);
    useDebugValue(`isScriptLoaded: ${String(values.isScriptLoaded)}`);
    useDebugValue(`isScriptError: ${String(values.isScriptError)}`);
    useDebugValue(`scriptError: ${values.scriptError}`);
    return values;
};
/** Loads Hubspot script to the document and syncs loading state between forms on the page */
const HubspotProvider = ({ children, strategy = "afterInteractive", src: passedSrc, onReady: passedOnReady, onError: passedOnError, ...props }) => {
    const [isScriptLoaded, setIsScriptLoaded] = useState(false);
    const [scriptError, setScriptError] = useState(null);
    const isScriptError = !!scriptError;
    const src = passedSrc || "https://js.hsforms.net/forms/v2.js";
    // Handle script load
    const onReady = useCallback(() => {
        setScriptError(null);
        setIsScriptLoaded(true);
        window.dispatchEvent(new Event(HUBSPOT_LOADED_EVENT));
        passedOnReady?.();
    }, [passedOnReady]);
    // Handle script error
    const onError = useCallback((e) => {
        console.error("HubSpot script failed to load:", e);
        setScriptError(e);
        passedOnError?.(e);
    }, [passedOnError]);
    // Prevent unnecessary rerenders
    const value = useMemo(() => ({
        isScriptLoaded,
        isScriptError,
        scriptError,
    }), [isScriptLoaded, isScriptError, scriptError]);
    return (React.createElement(HubspotContext.Provider, { value: value },
        children,
        React.createElement(Script, { src: src, strategy: strategy, onReady: onReady, onError: onError, ...props })));
};

export { HUBSPOT_LOADED_EVENT, HubspotContext, HubspotProvider, useHubspotContext };
